import {
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { DemoResetNotice } from "../reset";

/*
 * THESIS: A newsroom operations desk for moving one verified result feed from file to publication.
 * OWN-WORLD: The black count-board shell, gold state signal, narrow labels, and hard table rules extend the public product.
 * STORY: Authenticate, process a source file, choose the uploaded batch, import it, then activate or export a version.
 * FIRST VIEWPORT: Results opens on the upload control, real production seed, import action, and active table status.
 * FORM: Square operational surfaces and dense rows; radius is reserved for controls and status marks.
 */

type Permission = "upload" | "download" | "users";

type AdminUser = {
  id: string;
  name: string;
  email: string;
  permissions: Permission[];
  createdAt: string;
  updatedAt: string;
  seeded: boolean;
};

type UploadedFile = {
  name: string;
  originalName: string;
  size: number;
  uploadedAt: string;
  associatedDate: string | null;
  rowCount: number;
  status: "ready";
};

type ResultTable = {
  name: string;
  createdAt: string;
  rowCount: number;
  active: boolean;
};

type ProcessedFile = {
  sourceName: string;
  originalSize: number;
  columns: string[];
  summary: {
    rowCount: number;
    precinctCount: number;
    totalPrecincts: number | null;
    totalVotes: number;
    registeredVoters: number | null;
    receptionDate: string | null;
    contests: Array<{
      contestCode: string;
      contestName: string | null;
      candidates: Array<{
        candidateName: string;
        partyCode: string;
        partyAlias: string | null;
        votesAmount: number;
        totalizationOrder: number;
      }>;
    }>;
  };
};

type ImportProgress = {
  phase: string;
  processedBytes: number;
  totalBytes: number;
  percent: number;
};

type WorkerMessage =
  | ({ type: "progress"; id: string } & ImportProgress)
  | ({ type: "complete"; id: string } & ProcessedFile)
  | { type: "error"; id: string; message: string };

const importColumns = [
  "PRECINCT_CODE",
  "CONTEST_CODE",
  "CANDIDATE_NAME",
  "PARTY_CODE",
  "VOTES_AMOUNT",
  "TOTALIZATION_ORDER",
  "NUMBER_VOTERS",
  "UNDER_VOTES",
  "OVER_VOTES",
  "RECEPTION_DATE",
] as const;

const provinces = [
  "ABRA",
  "AGUSAN DEL NORTE",
  "AGUSAN DEL SUR",
  "AKLAN",
  "ALBAY",
  "ANTIQUE",
  "APAYAO",
  "AURORA",
  "BASILAN",
  "BATAAN",
  "BATANES",
  "BATANGAS",
  "BENGUET",
  "BILIRAN",
  "BOHOL",
  "BUKIDNON",
  "BULACAN",
  "CAGAYAN",
  "CAMARINES NORTE",
  "CAMARINES SUR",
  "CAMIGUIN",
  "CAPIZ",
  "CATANDUANES",
  "CAVITE",
  "CEBU",
  "COTABATO",
  "DAVAO DE ORO",
  "DAVAO DEL NORTE",
  "DAVAO DEL SUR",
  "DAVAO OCCIDENTAL",
  "DAVAO ORIENTAL",
  "DINAGAT ISLANDS",
  "EASTERN SAMAR",
  "GUIMARAS",
  "IFUGAO",
  "ILOCOS NORTE",
  "ILOCOS SUR",
  "ILOILO",
  "ISABELA",
  "KALINGA",
  "LA UNION",
  "LAGUNA",
  "LANAO DEL NORTE",
  "LANAO DEL SUR",
  "LEYTE",
  "MAGUINDANAO DEL NORTE",
  "MAGUINDANAO DEL SUR",
  "MARINDUQUE",
  "MASBATE",
  "METRO MANILA",
  "MISAMIS OCCIDENTAL",
  "MISAMIS ORIENTAL",
  "MOUNTAIN PROVINCE",
  "NEGROS OCCIDENTAL",
  "NEGROS ORIENTAL",
  "NORTHERN SAMAR",
  "NUEVA ECIJA",
  "NUEVA VIZCAYA",
  "OCCIDENTAL MINDORO",
  "ORIENTAL MINDORO",
  "PALAWAN",
  "PAMPANGA",
  "PANGASINAN",
  "QUEZON",
  "QUIRINO",
  "RIZAL",
  "ROMBLON",
  "SAMAR",
  "SARANGANI",
  "SIQUIJOR",
  "SORSOGON",
  "SOUTH COTABATO",
  "SOUTHERN LEYTE",
  "SPECIAL GEOGRAPHIC AREA",
  "SULTAN KUDARAT",
  "SULU",
  "SURIGAO DEL NORTE",
  "SURIGAO DEL SUR",
  "TARLAC",
  "TAWI-TAWI",
  "ZAMBALES",
  "ZAMBOANGA DEL NORTE",
  "ZAMBOANGA DEL SUR",
  "ZAMBOANGA SIBUGAY",
] as const;

const downloadReports = [
  {
    title: "Senatorial race results",
    description: "Ranked national Senate tally for graphics and editorial desks.",
    path: "/api/cms/download-senatorial-race-results",
  },
  {
    title: "Party-list race results",
    description: "Ranked national party-list tally from the active table.",
    path: "/api/cms/download-party-list-race-results",
  },
  {
    title: "NCR mayoral results",
    description: "Mayoral and vice-mayoral contests prepared for the NCR sheet.",
    path: "/api/cms/download-ncr-mayors-race-results",
  },
  {
    title: "Senatorial side stream",
    description: "Compact Senate export formatted for the side-stream layout.",
    path: "/api/cms/download-senatorial-race-results-side-stream",
  },
  {
    title: "House race results",
    description: "All congressional contests available in the active import.",
    path: "/api/cms/download-house-race-results",
  },
] as const;

const formatNumber = (value: number) => value.toLocaleString("en-PH");

const formatBytes = (value: number) => {
  if (value < 1024) return `${value} B`;
  const units = ["KB", "MB", "GB", "TB"] as const;
  let amount = value / 1024;
  let unitIndex = 0;
  while (amount >= 1024 && unitIndex < units.length - 1) {
    amount /= 1024;
    unitIndex += 1;
  }
  return `${amount.toFixed(amount >= 10 ? 1 : 2)} ${units[unitIndex]}`;
};

const formatDate = (value: string | null | undefined) => {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en-PH", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Manila",
  }).format(new Date(value));
};

const initialAssociatedDate = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
};

const routeTo = (href: string, replace = false) => {
  if (replace) {
    window.history.replaceState(null, "", href);
  } else {
    window.history.pushState(null, "", href);
  }
  window.dispatchEvent(new PopStateEvent("popstate"));
};

const workspaceForPermissions = (permissions: readonly Permission[]) => {
  if (permissions.includes("upload")) return "/cms";
  if (permissions.includes("download")) return "/cms/download-results";
  return "/cms/users";
};

const responseError = async (response: Response) => {
  const body: unknown = await response.json().catch(() => null);
  if (body && typeof body === "object" && "error" in body) {
    const error = body.error;
    if (typeof error === "string") return error;
    if (error && typeof error === "object" && "message" in error) {
      return String(error.message);
    }
  }
  return `Request failed with ${response.status}.`;
};

const adminRequest = async <Value,>(url: string, init?: RequestInit): Promise<Value> => {
  const response = await fetch(url, {
    credentials: "same-origin",
    headers: {
      accept: "application/json",
      ...(init?.body && typeof init.body === "string"
        ? { "content-type": "application/json" }
        : {}),
      ...init?.headers,
    },
    ...init,
  });
  if (!response.ok) {
    throw new Error(await responseError(response));
  }
  return (await response.json()) as Value;
};

const processResultFile = (file: File, onProgress: (progress: ImportProgress) => void) => {
  const worker = new Worker(new URL("./import.worker.ts", import.meta.url), {
    type: "module",
  });
  const id = crypto.randomUUID();
  const promise = new Promise<ProcessedFile>((resolve, reject) => {
    worker.addEventListener("message", (event: MessageEvent<WorkerMessage>) => {
      if (event.data.id !== id) return;
      if (event.data.type === "progress") {
        onProgress(event.data);
      } else if (event.data.type === "complete") {
        resolve(event.data);
        worker.terminate();
      } else {
        reject(new Error(event.data.message));
        worker.terminate();
      }
    });
    worker.addEventListener("error", () => {
      reject(new Error("The browser import worker stopped unexpectedly."));
      worker.terminate();
    });
    worker.postMessage({ id, file });
  });
  return { promise, cancel: () => worker.terminate() };
};

const StatusMessage = ({
  tone,
  children,
}: {
  tone: "success" | "error" | "info";
  children: ReactNode;
}) => (
  <div
    className={`admin-status admin-status--${tone}`}
    role={tone === "error" ? "alert" : "status"}
  >
    {children}
  </div>
);

const AdminMark = () => (
  <span className="admin-mark" aria-hidden="true">
    <span>Election</span>
    <strong>2025</strong>
    <small>Back office</small>
  </span>
);

const AdminDisclosure = () => (
  <footer className="admin-disclosure">
    <p>
      Portfolio demo: national tables may be imported snapshots; local public panels are synthetic
      and illustrative.
    </p>
    <a href="https://jericop.dev">Built by Jerico Pulvera</a>
  </footer>
);

export const AdminLogin = () => {
  const [state, setState] = useState<"checking" | "ready" | "working">("checking");
  const [email, setEmail] = useState("admin@vote-results.demo");
  const [accessCode, setAccessCode] = useState("demo-access");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void adminRequest<{ user: AdminUser | null }>("/api/auth/session")
      .then((session) => {
        if (active && session.user) {
          routeTo(workspaceForPermissions(session.user.permissions), true);
        } else if (active) {
          setState("ready");
        }
      })
      .catch(() => {
        if (active) setState("ready");
      });
    return () => {
      active = false;
    };
  }, []);

  const signIn = async (event: FormEvent) => {
    event.preventDefault();
    setState("working");
    setError(null);
    try {
      const response = await adminRequest<{ user: AdminUser }>("/api/auth/sign-in", {
        method: "POST",
        body: JSON.stringify({ email, accessCode }),
      });
      routeTo(workspaceForPermissions(response.user.permissions), true);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Sign-in failed.");
      setState("ready");
    }
  };

  return (
    <main className="admin-login">
      <section className="admin-login__panel" aria-labelledby="admin-login-title">
        <a className="admin-login__brand" href="/" aria-label="Return to public election results">
          <AdminMark />
        </a>
        <div className="admin-login__copy">
          <p className="admin-eyebrow">Operations access</p>
          <h1 id="admin-login-title">Results desk</h1>
          <p>
            Upload production result bundles, activate table versions, manage operators, and
            download newsroom files.
          </p>
        </div>
        {error ? <StatusMessage tone="error">{error}</StatusMessage> : null}
        <form className="admin-login__form" onSubmit={signIn}>
          <label className="admin-field">
            <span>Email address</span>
            <input
              type="email"
              autoComplete="username"
              required
              maxLength={200}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label className="admin-field">
            <span>Access code</span>
            <input
              type="password"
              autoComplete="current-password"
              required
              minLength={8}
              maxLength={100}
              value={accessCode}
              onChange={(event) => setAccessCode(event.target.value)}
            />
          </label>
          <button
            className="admin-button admin-button--primary admin-login__button"
            type="submit"
            disabled={state !== "ready"}
          >
            {state === "checking"
              ? "Checking session…"
              : state === "working"
                ? "Opening desk…"
                : "Sign in"}
          </button>
        </form>
        <p className="admin-login__note">
          Demo access is prefilled. New operators can sign in with the access code assigned in User
          management; their permissions control which workspaces they can open.
        </p>
      </section>
      <DemoResetNotice compact />
      <AdminDisclosure />
    </main>
  );
};

const ResultsWorkspace = () => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [tables, setTables] = useState<ResultTable[]>([]);
  const [logs, setLogs] = useState<string[]>([]);
  const [importStatus, setImportStatus] = useState("Loading import history…");
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [selectedUpload, setSelectedUpload] = useState<File | null>(null);
  const [associatedDate, setAssociatedDate] = useState(initialAssociatedDate);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const [working, setWorking] = useState<"upload" | "import" | "deploy" | null>(null);
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const activeImport = useRef<ReturnType<typeof processResultFile> | null>(null);

  const loadWorkspace = useCallback(async () => {
    const [fileResponse, tableResponse, logResponse] = await Promise.all([
      adminRequest<{ files: UploadedFile[] }>("/api/cms/files/list"),
      adminRequest<{ tables: ResultTable[] }>("/api/tables"),
      adminRequest<{ logs: string[]; status: string }>("/api/cms/results/import-logs"),
    ]);
    setFiles(fileResponse.files);
    setTables(tableResponse.tables);
    setLogs(logResponse.logs);
    setImportStatus(logResponse.status);
  }, []);

  useEffect(() => {
    let active = true;
    void loadWorkspace().catch((caught) => {
      if (active) {
        setMessage({
          tone: "error",
          text: caught instanceof Error ? caught.message : "The workspace could not be loaded.",
        });
      }
    });
    return () => {
      active = false;
      activeImport.current?.cancel();
    };
  }, [loadWorkspace]);

  const upload = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedUpload) {
      setMessage({ tone: "error", text: "Select a CSV file or result bundle first." });
      return;
    }
    setWorking("upload");
    setMessage(null);
    setProgress({
      phase: "Starting local processor",
      processedBytes: 0,
      totalBytes: selectedUpload.size,
      percent: 0,
    });
    try {
      const importTask = processResultFile(selectedUpload, setProgress);
      activeImport.current = importTask;
      const processed = await importTask.promise;
      setProgress({
        phase: "Registering processed result summary",
        processedBytes: processed.originalSize,
        totalBytes: processed.originalSize,
        percent: 100,
      });
      await adminRequest("/api/cms/files/register", {
        method: "POST",
        body: JSON.stringify({
          originalName: selectedUpload.name,
          size: selectedUpload.size,
          associatedDate: new Date(associatedDate).toISOString(),
          columns: processed.columns,
          summary: processed.summary,
        }),
      });
      setMessage({
        tone: "success",
        text: `${selectedUpload.name} is ready: ${formatNumber(processed.summary.rowCount)} rows across ${formatNumber(processed.summary.contests.length)} contests.`,
      });
      setSelectedUpload(null);
      setProgress(null);
      if (fileInput.current) fileInput.current.value = "";
      await loadWorkspace();
    } catch (caught) {
      setMessage({
        tone: "error",
        text: caught instanceof Error ? caught.message : "The file could not be processed.",
      });
    } finally {
      activeImport.current = null;
      setWorking(null);
    }
  };

  const runImport = async () => {
    setWorking("import");
    setMessage(null);
    try {
      const response = await adminRequest<{
        message: string;
        logs: string[];
      }>("/api/cms/import", {
        method: "POST",
        body: JSON.stringify({ filenames: selectedFiles }),
      });
      setLogs(response.logs);
      setImportStatus("Import completed; version awaiting activation");
      setMessage({ tone: "success", text: response.message });
      setSelectedFiles([]);
      await loadWorkspace();
    } catch (caught) {
      setMessage({
        tone: "error",
        text: caught instanceof Error ? caught.message : "The import could not be completed.",
      });
    } finally {
      setWorking(null);
    }
  };

  const deploy = async (tableName: string) => {
    setWorking("deploy");
    setMessage(null);
    try {
      await adminRequest("/api/tables/deploy", {
        method: "POST",
        body: JSON.stringify({ tableName }),
      });
      setMessage({ tone: "success", text: `${tableName} is now the active results table.` });
      await loadWorkspace();
    } catch (caught) {
      setMessage({
        tone: "error",
        text: caught instanceof Error ? caught.message : "The table could not be activated.",
      });
    } finally {
      setWorking(null);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([`${importColumns.join(",")}\r\n`], {
      type: "text/csv;charset=utf-8",
    });
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = "election_results_import_template.csv";
    anchor.click();
    URL.revokeObjectURL(href);
  };

  const chooseFile = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedUpload(event.target.files?.[0] ?? null);
    setMessage(null);
    setProgress(null);
  };

  return (
    <div className="admin-page-stack">
      <section className="admin-section admin-upload" aria-labelledby="upload-title">
        <div className="admin-section__heading">
          <div>
            <p className="admin-eyebrow">Source intake</p>
            <h2 id="upload-title">Upload results</h2>
          </div>
          <button
            className="admin-button admin-button--quiet"
            type="button"
            onClick={downloadTemplate}
          >
            Download CSV template
          </button>
        </div>
        <form className="admin-upload__form" onSubmit={upload}>
          <label className="admin-file-picker">
            <span>Result file</span>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.zip,text/csv,application/zip"
              onChange={chooseFile}
            />
            <strong>{selectedUpload?.name ?? "Choose CSV or production bundle"}</strong>
            <small>
              ZIP bundles are opened locally; only the validated aggregate is sent to the back
              office.
            </small>
          </label>
          <label className="admin-field">
            <span>Associated date and time</span>
            <input
              type="datetime-local"
              required
              value={associatedDate}
              onChange={(event) => setAssociatedDate(event.target.value)}
            />
          </label>
          <button
            className="admin-button admin-button--primary"
            type="submit"
            disabled={Boolean(working)}
          >
            {working === "upload" ? "Processing file…" : "Upload results"}
          </button>
        </form>
        {progress ? (
          <div className="admin-progress" role="status" aria-live="polite">
            <div>
              <strong>{progress.phase}</strong>
              <span>{progress.percent.toFixed(1)}%</span>
            </div>
            <progress max={100} value={progress.percent}>
              {progress.percent}%
            </progress>
            <small>
              {formatBytes(progress.processedBytes)} of {formatBytes(progress.totalBytes)}
            </small>
          </div>
        ) : null}
        {message ? <StatusMessage tone={message.tone}>{message.text}</StatusMessage> : null}
      </section>

      <section className="admin-section" aria-labelledby="available-files-title">
        <div className="admin-section__heading">
          <div>
            <p className="admin-eyebrow">Staging area</p>
            <h2 id="available-files-title">Available files</h2>
          </div>
          <button
            className="admin-button admin-button--primary"
            type="button"
            onClick={runImport}
            disabled={selectedFiles.length === 0 || Boolean(working)}
          >
            {working === "import"
              ? "Importing…"
              : `Import selected${selectedFiles.length ? ` (${selectedFiles.length})` : ""}`}
          </button>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <caption className="sr-only">Uploaded result files ready for import</caption>
            <thead>
              <tr>
                <th scope="col">
                  <span className="sr-only">Select</span>
                </th>
                <th scope="col">File</th>
                <th scope="col">Associated</th>
                <th scope="col">Rows</th>
                <th scope="col">Size</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <tr key={file.name}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${file.originalName}`}
                      checked={selectedFiles.includes(file.name)}
                      onChange={(event) =>
                        setSelectedFiles((current) =>
                          event.target.checked
                            ? [...current, file.name]
                            : current.filter((name) => name !== file.name),
                        )
                      }
                    />
                  </td>
                  <td>
                    <strong>{file.originalName}</strong>
                    <small>{file.name}</small>
                  </td>
                  <td>{formatDate(file.associatedDate)}</td>
                  <td>{formatNumber(file.rowCount)}</td>
                  <td>{formatBytes(file.size)}</td>
                  <td>
                    <span className="admin-state-dot">Ready</span>
                  </td>
                </tr>
              ))}
              {files.length === 0 ? (
                <tr>
                  <td colSpan={6}>No uploaded files are available.</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <div className="admin-workspace-grid">
        <section className="admin-section" aria-labelledby="tables-title">
          <div className="admin-section__heading">
            <div>
              <p className="admin-eyebrow">Publication state</p>
              <h2 id="tables-title">Result tables</h2>
            </div>
          </div>
          <div className="admin-table-wrap">
            <table className="admin-table admin-table--compact">
              <caption className="sr-only">Imported result table versions</caption>
              <thead>
                <tr>
                  <th scope="col">Table</th>
                  <th scope="col">Rows</th>
                  <th scope="col">Created</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {tables.map((table) => (
                  <tr key={`${table.name}-${table.active}`}>
                    <td>
                      <strong>{table.name}</strong>
                      {table.active ? <small>Active public source</small> : null}
                    </td>
                    <td>{formatNumber(table.rowCount)}</td>
                    <td>{formatDate(table.createdAt)}</td>
                    <td>
                      {table.active ? (
                        <span className="admin-state-dot">Live</span>
                      ) : (
                        <button
                          className="admin-text-button"
                          type="button"
                          onClick={() => deploy(table.name)}
                          disabled={Boolean(working)}
                        >
                          Activate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="admin-section admin-log" aria-labelledby="import-log-title">
          <div className="admin-section__heading">
            <div>
              <p className="admin-eyebrow">Last run</p>
              <h2 id="import-log-title">Import log</h2>
            </div>
            <span className="admin-state-dot">{importStatus}</span>
          </div>
          {logs.length ? (
            <ol>
              {logs.map((log) => (
                <li key={log}>{log}</li>
              ))}
            </ol>
          ) : (
            <p>Select an available file and run the import to create a table version.</p>
          )}
        </section>
      </div>
    </div>
  );
};

const UserWorkspace = () => {
  const emptyForm = {
    id: null as string | null,
    name: "",
    email: "",
    accessCode: "",
    permissions: ["upload", "download"] as Permission[],
  };
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  const loadUsers = useCallback(async () => {
    const response = await adminRequest<{ users: AdminUser[] }>("/api/cms/users");
    setUsers(response.users);
  }, []);

  useEffect(() => {
    void loadUsers().catch((caught) =>
      setMessage({
        tone: "error",
        text: caught instanceof Error ? caught.message : "Users could not be loaded.",
      }),
    );
  }, [loadUsers]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setWorking(true);
    setMessage(null);
    try {
      await adminRequest(form.id ? `/api/cms/users/${form.id}` : "/api/cms/users", {
        method: form.id ? "PATCH" : "POST",
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          permissions: form.permissions,
          ...(form.accessCode ? { accessCode: form.accessCode } : {}),
        }),
      });
      setMessage({
        tone: "success",
        text: form.id ? "Operator access updated." : "Operator access created.",
      });
      setForm(emptyForm);
      await loadUsers();
    } catch (caught) {
      setMessage({
        tone: "error",
        text: caught instanceof Error ? caught.message : "The operator could not be saved.",
      });
    } finally {
      setWorking(false);
    }
  };

  const edit = (user: AdminUser) => {
    setForm({
      id: user.id,
      name: user.name,
      email: user.email,
      accessCode: "",
      permissions: user.permissions,
    });
    setMessage(null);
    document.getElementById("operator-name")?.focus();
  };

  const remove = async (user: AdminUser) => {
    if (!window.confirm(`Remove access for ${user.name}?`)) return;
    setWorking(true);
    setMessage(null);
    try {
      await adminRequest(`/api/cms/users/${user.id}`, { method: "DELETE" });
      setMessage({ tone: "success", text: `${user.name} was removed.` });
      await loadUsers();
    } catch (caught) {
      setMessage({
        tone: "error",
        text: caught instanceof Error ? caught.message : "The operator could not be removed.",
      });
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="admin-page-stack">
      <section className="admin-section" aria-labelledby="users-title">
        <div className="admin-section__heading">
          <div>
            <p className="admin-eyebrow">Access control</p>
            <h2 id="users-title">Back-office users</h2>
          </div>
          <span>{users.length} operators</span>
        </div>
        {message ? <StatusMessage tone={message.tone}>{message.text}</StatusMessage> : null}
        <div className="admin-table-wrap">
          <table className="admin-table">
            <caption className="sr-only">Users with back-office access</caption>
            <thead>
              <tr>
                <th scope="col">Operator</th>
                <th scope="col">Permissions</th>
                <th scope="col">Updated</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <strong>{user.name}</strong>
                    <small>{user.email}</small>
                  </td>
                  <td>
                    <div className="admin-permission-list">
                      {user.permissions.map((permission) => (
                        <span key={permission}>{permission}</span>
                      ))}
                    </div>
                  </td>
                  <td>{formatDate(user.updatedAt)}</td>
                  <td>
                    <div className="admin-row-actions">
                      <button
                        className="admin-text-button"
                        type="button"
                        onClick={() => edit(user)}
                        disabled={user.seeded || working}
                        title={user.seeded ? "The demo administrator is permanent" : undefined}
                      >
                        Edit
                      </button>
                      <button
                        className="admin-text-button admin-text-button--danger"
                        type="button"
                        onClick={() => remove(user)}
                        disabled={user.seeded || working}
                        title={user.seeded ? "The demo administrator is permanent" : undefined}
                      >
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="admin-section admin-user-form" aria-labelledby="user-form-title">
        <div className="admin-section__heading">
          <div>
            <p className="admin-eyebrow">{form.id ? "Edit access" : "New access"}</p>
            <h2 id="user-form-title">{form.id ? "Update operator" : "Add operator"}</h2>
          </div>
          {form.id ? (
            <button
              className="admin-button admin-button--quiet"
              type="button"
              onClick={() => setForm(emptyForm)}
            >
              Cancel edit
            </button>
          ) : null}
        </div>
        <form onSubmit={submit}>
          <label className="admin-field">
            <span>Name</span>
            <input
              id="operator-name"
              required
              minLength={2}
              maxLength={100}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </label>
          <label className="admin-field">
            <span>Email address</span>
            <input
              type="email"
              required
              maxLength={200}
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
            />
          </label>
          <label className="admin-field">
            <span>{form.id ? "New access code (optional)" : "Access code"}</span>
            <input
              type="password"
              autoComplete="new-password"
              required={!form.id}
              minLength={8}
              maxLength={100}
              value={form.accessCode}
              onChange={(event) => setForm({ ...form, accessCode: event.target.value })}
            />
            <small>
              {form.id
                ? "Leave blank to keep the operator’s current code."
                : "Use at least eight characters and share it with the operator securely."}
            </small>
          </label>
          <fieldset>
            <legend>Permissions</legend>
            {(["upload", "download", "users"] as const).map((permission) => (
              <label key={permission}>
                <input
                  type="checkbox"
                  checked={form.permissions.includes(permission)}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      permissions: event.target.checked
                        ? [...form.permissions, permission]
                        : form.permissions.filter((item) => item !== permission),
                    })
                  }
                />
                <span>{permission}</span>
              </label>
            ))}
          </fieldset>
          <button className="admin-button admin-button--primary" type="submit" disabled={working}>
            {working ? "Saving…" : form.id ? "Save changes" : "Create operator"}
          </button>
        </form>
      </section>
    </div>
  );
};

const DownloadButtons = ({ path, province }: { path: string; province?: string }) => (
  <div className="admin-download-buttons">
    {(["csv", "xls", "xlsx"] as const).map((fileType) => {
      const query = new URLSearchParams({ fileType });
      if (province) query.set("province", province);
      return (
        <a key={fileType} href={`${path}?${query.toString()}`} download>
          {fileType.toUpperCase()}
        </a>
      );
    })}
  </div>
);

const DownloadWorkspace = () => {
  const [province, setProvince] = useState<(typeof provinces)[number]>("ABRA");
  return (
    <div className="admin-page-stack">
      <section className="admin-section" aria-labelledby="downloads-title">
        <div className="admin-section__heading">
          <div>
            <p className="admin-eyebrow">Newsroom output</p>
            <h2 id="downloads-title">Download results</h2>
          </div>
          <span>Active table only</span>
        </div>
        <div className="admin-download-list">
          {downloadReports.map((report, index) => (
            <article key={report.path}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <div>
                <h3>{report.title}</h3>
                <p>{report.description}</p>
              </div>
              <DownloadButtons path={report.path} />
            </article>
          ))}
        </div>
      </section>

      <section className="admin-section admin-province-download" aria-labelledby="province-title">
        <div className="admin-section__heading">
          <div>
            <p className="admin-eyebrow">Provincial output</p>
            <h2 id="province-title">Mayoral results by province</h2>
          </div>
        </div>
        <div>
          <label className="admin-field">
            <span>Province</span>
            <select
              value={province}
              onChange={(event) => setProvince(event.target.value as (typeof provinces)[number])}
            >
              {provinces.map((name) => (
                <option key={name}>{name}</option>
              ))}
            </select>
          </label>
          <DownloadButtons
            path="/api/cms/download-mayors-race-results-by-province"
            province={province}
          />
        </div>
      </section>
    </div>
  );
};

const sectionFromPath = (pathname: string) => {
  if (pathname.startsWith("/cms/users")) return "users";
  if (pathname.startsWith("/cms/download-results")) return "downloads";
  return "results";
};

export const AdminApp = ({ pathname }: { pathname: string }) => {
  const [session, setSession] = useState<{ user: AdminUser } | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const section = sectionFromPath(pathname);
  const requiredPermission: Permission =
    section === "users" ? "users" : section === "downloads" ? "download" : "upload";

  useEffect(() => {
    let active = true;
    void adminRequest<{ user: AdminUser | null }>("/api/auth/session")
      .then((value) => {
        if (!active) return;
        if (value.user) {
          setSession({ user: value.user });
        } else {
          routeTo("/login", true);
        }
      })
      .catch((caught) => {
        if (!active) return;
        const message = caught instanceof Error ? caught.message : "Session unavailable.";
        if (message === "Unauthorized") {
          routeTo("/login", true);
        } else {
          setSessionError(message);
        }
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (session && !session.user.permissions.includes(requiredPermission)) {
      routeTo(workspaceForPermissions(session.user.permissions), true);
    }
  }, [requiredPermission, session]);

  const signOut = async () => {
    try {
      await adminRequest("/api/auth/sign-out", { method: "POST" });
    } finally {
      routeTo("/login", true);
    }
  };

  if (sessionError) {
    return (
      <main className="admin-fatal">
        <StatusMessage tone="error">{sessionError}</StatusMessage>
        <a href="/login">Return to sign in</a>
      </main>
    );
  }
  if (!session) {
    return (
      <main className="admin-loading" aria-busy="true">
        <AdminMark />
        <p>Opening results desk…</p>
      </main>
    );
  }
  if (!session.user.permissions.includes(requiredPermission)) {
    return (
      <main className="admin-loading" aria-busy="true">
        <AdminMark />
        <p>Opening your permitted workspace…</p>
      </main>
    );
  }

  const nav = [
    { key: "results", href: "/cms", label: "Results", permission: "upload" },
    { key: "users", href: "/cms/users", label: "Users", permission: "users" },
    {
      key: "downloads",
      href: "/cms/download-results",
      label: "Download results",
      permission: "download",
    },
  ] as const;
  const activeLabel = nav.find((item) => item.key === section)?.label ?? "Results";

  return (
    <div className="admin-shell">
      <a className="admin-skip-link" href="#admin-main">
        Skip to workspace
      </a>
      <aside className="admin-sidebar">
        <a className="admin-sidebar__brand" href="/cms" aria-label="Results back office home">
          <AdminMark />
        </a>
        <nav aria-label="Back-office navigation">
          {nav
            .filter((item) => session.user.permissions.includes(item.permission))
            .map((item, index) => (
              <a
                key={item.key}
                href={item.href}
                aria-current={section === item.key ? "page" : undefined}
              >
                <span>{String(index + 1).padStart(2, "0")}</span>
                <strong>{item.label}</strong>
              </a>
            ))}
        </nav>
        <div className="admin-sidebar__account">
          <span>{session.user.name.slice(0, 1).toLocaleUpperCase()}</span>
          <div>
            <strong>{session.user.name}</strong>
            <small>{session.user.email}</small>
          </div>
          <button type="button" onClick={signOut}>
            Sign out
          </button>
        </div>
      </aside>

      <div className="admin-workspace">
        <header className="admin-topbar">
          <div>
            <p>Election results / Back office</p>
            <h1>{activeLabel}</h1>
          </div>
          <a href="/">View public results</a>
        </header>
        <DemoResetNotice compact />
        <main id="admin-main">
          {section === "users" ? (
            <UserWorkspace />
          ) : section === "downloads" ? (
            <DownloadWorkspace />
          ) : (
            <ResultsWorkspace />
          )}
        </main>
        <AdminDisclosure />
      </div>
    </div>
  );
};
